import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { MessageSquareText, X } from "lucide-react";
import { api } from "../services/api";
import { useAuth } from "../auth/AuthContext";

const REVIEW_SNOOZE_KEY = "bobbinusa_review_snoozes";
const REVIEW_SNOOZE_MS = 24 * 1000;

function reviewProductId(item) {
  return String(item?.productId || item?.sku || item?.name || "");
}

function reviewItemKey(order, item) {
  return `${order?._id || order?.id}:${reviewProductId(item)}`;
}

function readReviewSnoozes() {
  try {
    return JSON.parse(localStorage.getItem(REVIEW_SNOOZE_KEY) || "{}");
  } catch {
    return {};
  }
}

export default function ReviewReminderAlert() {
  const { isAuthenticated, user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [snoozes, setSnoozes] = useState(readReviewSnoozes);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let mounted = true;
    if (!isAuthenticated || user?.role !== "user") {
      setOrders([]);
      setReviews([]);
      setLoaded(true);
      return undefined;
    }

    setLoaded(false);
    Promise.all([
      api.get("/orders/my").catch(() => ({ orders: [] })),
      api.get("/reviews/my").catch(() => ({ reviews: [] })),
    ])
      .then(([orderData, reviewData]) => {
        if (!mounted) return;
        setOrders(orderData.orders || []);
        setReviews(reviewData.reviews || []);
      })
      .finally(() => {
        if (mounted) setLoaded(true);
      });

    return () => {
      mounted = false;
    };
  }, [isAuthenticated, user?.role, user?._id, user?.id]);

  const pendingReview = useMemo(() => {
    const now = Date.now();
    return orders.flatMap((order) => {
      if (order.status !== "delivered") return [];
      return (order.items || []).map((item) => ({ order, item }));
    }).find(({ order, item }) => {
      const productId = reviewProductId(item);
      const key = reviewItemKey(order, item);
      if (!productId) return false;
      if (snoozes[key] && Number(snoozes[key]) > now) return false;
      return !reviews.some((review) => {
        const reviewedOrderId = String(review.order?._id || review.order || "");
        return reviewedOrderId === String(order._id) && String(review.productId) === productId;
      });
    });
  }, [orders, reviews, snoozes]);

  const closeAlert = () => {
    if (!pendingReview) return;
    const key = reviewItemKey(pendingReview.order, pendingReview.item);
    const next = { ...snoozes, [key]: Date.now() + REVIEW_SNOOZE_MS };
    setSnoozes(next);
    localStorage.setItem(REVIEW_SNOOZE_KEY, JSON.stringify(next));
  };

  if (!loaded || !pendingReview) return null;

  const itemName = pendingReview.item?.name || "your delivered item";

  return (
    <div className="review-reminder-alert" role="status" aria-live="polite">
      <div className="review-reminder-alert__inner">
        <span className="review-reminder-alert__icon">
          <MessageSquareText size={18} />
        </span>
        <div className="review-reminder-alert__copy">
          <strong>Review your delivered item</strong>
          <span>{itemName}</span>
        </div>
        <Link className="review-reminder-alert__link" to="/my-account">
          Write review
        </Link>
        <button className="review-reminder-alert__close" onClick={closeAlert} type="button" aria-label="Close review reminder">
          <X size={16} />
        </button>
      </div>
    </div>
  );
}
